import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Assessment as ReportIcon,
  TrendingUp as TrendingUpIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';

// Import design system components
import { Card, CardContent, EmptyState } from '../../design-system';

// Import components
import PageHeader from '../../components/ui/PageHeader';
import TabPanel from '../../components/ui/TabPanel';
import MetricCard from '../../components/ui/MetricCard';

// Import stores
import usePayrollStore from '../../stores/payrollStore';
import useNotificationStore from '../../stores/notificationStore';
import useSettingsStore from '../../stores/settingsStore';

const PayrollReports = () => {
  const { showSuccess, showError } = useNotificationStore();
  const {
    payrollSummary,
    isLoading,
    fetchPayrollSummary
  } = usePayrollStore();
  const { hrSettings, fetchHRSettings } = useSettingsStore();

  const [tabValue, setTabValue] = useState(0);
  const [reportType, setReportType] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [department, setDepartment] = useState('');

  useEffect(() => {
    // Fetch HR settings when component mounts
    fetchHRSettings();
  }, []);

  useEffect(() => {
    loadReports();
  }, [reportType, selectedMonth, selectedYear, department]);

  const loadReports = async () => {
    try {
      await fetchPayrollSummary({
        type: reportType,
        month: selectedMonth,
        year: selectedYear,
        department
      });
    } catch (error) {
      showError('Failed to load reports');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleExport = (format) => {
    // Implement export functionality
    showSuccess(`Exporting report as ${format.toUpperCase()}...`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Map backend data structure to frontend expectations
  const summaryData = payrollSummary ? {
    totalEmployees: payrollSummary.employeesPaid || payrollSummary.totalEmployees || 0,
    totalGrossPay: (payrollSummary.breakdown?.basicSalary || 0) + (payrollSummary.breakdown?.additions || 0),
    totalDeductions: (payrollSummary.breakdown?.deductions || 0) + (payrollSummary.breakdown?.tax || 0),
    totalNetPay: payrollSummary.breakdown?.netSalary || payrollSummary.totalPayroll || 0,
    averageSalary: payrollSummary.averageSalary || 0
  } : {
    totalEmployees: 0,
    totalGrossPay: 0,
    totalDeductions: 0,
    totalNetPay: 0,
    averageSalary: 0
  };

  const departmentBreakdown = payrollSummary?.departmentBreakdown || [];

  const payrollTrends = payrollSummary?.trends || [];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Payroll Reports"
        subtitle="Comprehensive payroll analytics and reporting"
      />

      {/* Report Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  label="Report Type"
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Month"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString('default', { month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Year"
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  label="Department"
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {hrSettings?.departments?.filter(dept => dept.active !== false).map((dept) => (
                    <MenuItem key={dept._id} value={dept._id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadReports}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('pdf')}
                >
                  Export PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('excel')}
                >
                  Export Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Total Employees"
            value={summaryData.totalEmployees}
            icon={<ReportIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Total Gross Pay"
            value={formatCurrency(summaryData.totalGrossPay)}
            icon={<TrendingUpIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Total Deductions"
            value={formatCurrency(summaryData.totalDeductions)}
            icon={<PieChartIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Total Net Pay"
            value={formatCurrency(summaryData.totalNetPay)}
            icon={<TrendingUpIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Average Salary"
            value={formatCurrency(summaryData.averageSalary)}
            icon={<ReportIcon />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Report Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Department Breakdown" />
            <Tab label="Payroll Trends" />
            <Tab label="Detailed Report" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Department</TableCell>
                  <TableCell align="right">Employees</TableCell>
                  <TableCell align="right">Total Pay</TableCell>
                  <TableCell align="right">Average Pay</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departmentBreakdown.length > 0 ? (
                  departmentBreakdown.map((dept) => (
                    <TableRow key={dept.department}>
                      <TableCell>{dept.department}</TableCell>
                      <TableCell align="right">{dept.employees}</TableCell>
                      <TableCell align="right">{formatCurrency(dept.totalPay)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(dept.totalPay / dept.employees)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No department data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payroll Trends (Last 6 Months)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="right">Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payrollTrends.length > 0 ? (
                    payrollTrends.map((trend, index) => {
                      const previousAmount = index > 0 ? payrollTrends[index - 1].amount : trend.amount;
                      const change = ((trend.amount - previousAmount) / previousAmount * 100).toFixed(1);
                      return (
                        <TableRow key={trend.month}>
                          <TableCell>{trend.month}</TableCell>
                          <TableCell align="right">{formatCurrency(trend.amount)}</TableCell>
                          <TableCell align="right">
                            {index > 0 ? `${change}%` : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No trend data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            <EmptyState
              title="Detailed Reports Coming Soon"
              description="We're working on bringing you comprehensive detailed payroll reports with employee breakdowns, pay components, deductions, and advanced analytics. This feature will be available in the next update."
              icon={<ReportIcon />}
              action={
                <Button
                  variant="contained"
                  onClick={() => handleExport('pdf')}
                >
                  Export Current Summary
                </Button>
              }
            />
          </Box>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default PayrollReports;